import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { css } from 'react-emotion';
import moment from 'moment';
import uniq from 'lodash/uniq';

import SelectPartyStep, { EXISTING } from './SelectPartyStep';
import { Step, Stepper } from '@cimpress/react-components';
import AddProductsStep from './AddProductsStep';
import SelectCreationTypeStep from './SelectCreationTypeStep';
import AddContractDetailsStep from './AddContractDetailsStep';
import { actions as termActions } from '../../../../reducers/terms';
import ReviewStep from './ReviewStep';
import ResultStep from './ResultStep';
import { TermTypeLabel } from '../../../../constants/termTypes';
import { UNAVAILABLE, AVAILABLE, LOADING } from '../../../../constants/entityStatus';
import { withTranslation } from 'react-i18next';
import { formValueSelector } from 'redux-form';
import { fetchAccountIdForTheGivenFulfiller } from '../../../../services/FulfillerIdentity';
import { accountIdClaimName, getPricingArchVersion, pricingArchOldVersion } from '../../../../services/PricingArchInfo';
import { RedirectToContractManagerV2 } from '../../../home/landingPage/RedirectToContractManagerV2';
import { auth } from '../../../../utils/auth';

const initialValues = {
  creationType: EXISTING,
  dateSelection: {
    noEndDateChecked: true,
    endDate: undefined,
    startDate: moment.utc()
  },
  pricingContractType: TermTypeLabel.LIST_PRICE
};

const wizardBodyStyle = css`
  background-color: #fff;
  border: 1px solid #e1e6eb;
  border-radius: 4px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  padding: 30px 30px 30px;
`;

const stepperStyle = css`
  padding: 0px 50px;
`;

class ContractCreatePage extends PureComponent {
  static propTypes = {
    createPricingTerm: PropTypes.func.isRequired,
    updateTermItems: PropTypes.func.isRequired,
    fetchTermRevisionItems: PropTypes.func.isRequired
  };

  state = {
    step: 0,
    contract: null,
    contractStatus: UNAVAILABLE,
    showRedirection: null
  };

  nextStep = ({ numOfSteps = 1 }) => {
    if (this.state.step + numOfSteps === 1) {
      fetchAccountIdForTheGivenFulfiller(this.props.seller.value).then(accountId => this.showPopup(accountId));
    }
    this.setState({ step: this.state.step + numOfSteps });
  };

  showPopup = async accountId => {
    try {
      getPricingArchVersion(accountId).then(version => {
        if (version !== pricingArchOldVersion) {
          this.setState({ showRedirection: true });
        }
      });
    } finally {
      this.setState({ showRedirection: false });
    }
  };

  prevStep = ({ numOfSteps = 1 }) => {
    this.setState({ step: this.state.step - numOfSteps });
  };

  setStep = step => {
    this.setState({ step });
  };

  newContract = async pricingTerm => {
    const { createPricingTerm } = this.props;

    this.setState({ contractStatus: LOADING, step: this.state.step + 1 });

    try {
      const contract = await createPricingTerm(pricingTerm);
      this.setState({ contractStatus: AVAILABLE, contract });
    } catch (e) {
      this.setState({ contractStatus: UNAVAILABLE });
    }
  };

  updateContract = async (selectedContract, skuList) => {
    const { fetchTermRevisionItems, updateTermItems } = this.props;

    this.setState({ contractStatus: LOADING, step: this.state.step + 1 });

    try {
      const items = await fetchTermRevisionItems(selectedContract.termId, selectedContract.revisionId);
      const existingSkuList = items.map(item => item.sku);

      skuList = uniq(skuList.concat(existingSkuList));

      const { payload } = await updateTermItems(selectedContract.termId, selectedContract.revisionId, skuList);
      const contract = payload.statusResponse;
      this.setState({ contractStatus: AVAILABLE, contract });
    } catch (e) {
      this.setState({ contractStatus: UNAVAILABLE });
    }
  };

  renderSteps() {
    const { t } = this.props;

    const steps = [
      { title: t('createContract:getStarted.buyerTitle') },
      { title: t('createContract:addProducts.title') },
      { title: t('createContract:selectCreationTypeStep.title') },
      { title: t('createContract:pricingContract.title') },
      { title: t('createContract:review.title') }
    ];

    return steps.map((step, idx) => {
      return (
        <Step key={step.title} name={step.title} onClick={idx <= this.state.step ? () => this.setStep(idx) : null}>
          <div>
            <strong>{step.title}</strong>
          </div>
        </Step>
      );
    });
  }

  closeRedirectionPopup = () => {
    this.setState({ showRedirection: false });
  };

  render() {
    const { t } = this.props;
    const { contract, contractStatus, step, showRedirection } = this.state;

    const isFinalStep = step === 5;
    const steps = !isFinalStep ? (
      <div className={stepperStyle}>
        <Stepper activeStep={step}>{this.renderSteps()}</Stepper>
      </div>
    ) : null;

    let display;
    const profile = auth.getProfile();

    const enableContractsV1Application = process.env.REACT_APP_ENABLE_CONTRACT_V1_CREATION.split(',');
    if (showRedirection) {
      return (
        <RedirectToContractManagerV2
          enableCloseButton={enableContractsV1Application.includes(profile[accountIdClaimName])}
          onload={false}
          seller={this.props.seller}
          stayOnContractV1={this.closeRedirectionPopup}
        />
      );
    }

    switch (step) {
      case 0:
        display = <SelectPartyStep initialValues={initialValues} onSubmit={this.nextStep} />;
        break;
      case 1:
        display = <AddProductsStep onPrev={this.prevStep} onSubmit={this.nextStep} />;
        break;
      case 2:
        display = <SelectCreationTypeStep onPrev={this.prevStep} onSubmit={this.nextStep} />;
        break;
      case 3:
        display = <AddContractDetailsStep onPrev={this.prevStep} onSubmit={this.nextStep} />;
        break;
      case 4:
        display = (
          <ReviewStep
            onPrev={this.prevStep}
            newContract={this.newContract}
            updateContract={this.updateContract}
            nextStep={this.nextStep}
          />
        );
        break;
      case 5:
        display = <ResultStep contract={contract} contractStatus={contractStatus} />;
        break;
      default:
        display = <div>{t('errors:error_general')}</div>;
    }

    return (
      <div>
        {steps}
        <div className={wizardBodyStyle}>{display}</div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  const formSelector = formValueSelector('buyerCreateFlow');
  return {
    seller: formSelector(state, 'seller')
  };
}
const mapDispatchToProps = {
  createPricingTerm: termActions.createPricingTerm,
  updateTermItems: termActions.updateTermItems,
  fetchTermRevisionItems: termActions.fetchTermRevisionItems
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(ContractCreatePage));
